import React, { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PricingCard } from '../components/pricing/PricingCard'
import { STRIPE_PRODUCTS, formatPrice } from '../stripe-config'
import { useAuth } from '../hooks/useAuth'
import { PageLayout } from '../components/layout/PageLayout'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'

export function Pricing() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedPlan = searchParams.get('plan')
  const hasTriggeredCheckout = useRef(false)

  const handleLoginRequired = () => {
    navigate('/login')
  }

  useEffect(() => {
    if (user && selectedPlan && !loading && !hasTriggeredCheckout.current) {
      hasTriggeredCheckout.current = true
      setTimeout(() => {
        const planElement = document.querySelector(`[data-price-id="${selectedPlan}"]`)
        if (planElement) {
          (planElement as HTMLButtonElement).click()
        }
        setSearchParams({})
      }, 500)
    }
  }, [user, selectedPlan, loading, setSearchParams])

  return (
    <PageLayout>
      <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Choose Your Plan
            </h1>
            <p className="text-base sm:text-xl text-gray-600 mb-8 sm:mb-12 px-4">
              Select the perfect plan for your social media needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-[1400px] mx-auto mb-12">
            {STRIPE_PRODUCTS.filter(product => !product.isEnterprise).map((product) => (
              <PricingCard
                key={product.priceId}
                product={product}
                isAuthenticated={!!user}
                onLoginRequired={handleLoginRequired}
              />
            ))}
          </div>

          <div className="max-w-[1400px] mx-auto">
            {STRIPE_PRODUCTS.filter(product => product.isEnterprise).map((product) => (
              <Card key={product.priceId} className="border-2 border-purple-600 shadow-2xl bg-gradient-to-r from-purple-50 to-white">
                <div className="flex flex-col lg:flex-row">
                  <div className="lg:w-1/3 p-8 border-b lg:border-b-0 lg:border-r border-purple-200">
                    <div className="mb-4">
                      <span className="inline-block px-4 py-1 text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-purple-500 rounded-full">
                        ENTERPRISE
                      </span>
                    </div>
                    <CardTitle className="text-3xl text-purple-900 mb-3">{product.name}</CardTitle>
                    <CardDescription className="text-gray-700 mb-6">{product.description}</CardDescription>
                    <div className="text-4xl font-bold text-purple-900 mb-6">
                      {formatPrice(product.price, product.currency)}
                    </div>
                    <Button
                      onClick={() => navigate('/contact')}
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg text-lg font-semibold"
                    >
                      Contact Us
                    </Button>
                  </div>
                  <div className="lg:w-2/3 p-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Everything you need for your team</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {product.features?.map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

        </div>
      </div>
    </PageLayout>
  )
}

export default Pricing
